/* eslint-disable no-console */
/**
 * Modal de clasificación de una BOLETA DE HONORARIOS, para la tabla de Compras (Fase 3c).
 *
 * Pieza NUEVA a propósito: `AsignarRecibidaModal` está cableado a `facturas_sii_recibidas`
 * (su `handleAsignar` llama `vincularFacturaRecibida`, escribe `estado_vinculacion`, crea
 * activos, etc.). Una boleta no es un DTE recibido: se escribe directo en
 * `boletas_honorarios.cuenta_er` / `.tipo_contable`, columnas que ya existen desde Fase 1.
 *
 * Cierra el loop de Fases 1-2: hasta ahora las boletas entraban a `costosER` pero, sin cuenta,
 * caían en sin-clasificar para siempre. Con `cuenta_er` seteada devengan en su línea del ER
 * (la fuente ya está cableada, `costosER.js`); marcadas `activo` salen de la línea de costo
 * (el motor excluye `tipo_contable='activo'`).
 *
 * NO toca `computa` / `status` / `monto_*`: la clasificación es ortogonal a que la boleta
 * devengue o no (una nula clasificada sigue sin computar).
 *
 * Bloque B (2026-08): al marcar 'activo' se cierra el path a CapEx — la boleta puede reconciliar
 * un activo planificado (→ estado 'activo' + fecha_compra = arranca depreciación) o crear uno nuevo,
 * colgándose de `activos.boleta_honorario_id`. Reusa crearActivo/actualizarActivo (mismas funciones
 * que el camino SII: AsignarRecibidaModal y handleConfirmarActivo).
 */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER, TIPOS_ACTIVO, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { crearActivo, actualizarActivo, getActivosPlanificados } from "../../utils/activos";
import { actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));

const formatMonto = (monto) => (monto == null ? "-" : `$ ${Number(monto).toLocaleString("es-CL")}`);

const ClasificarHonorarioModal = ({ boleta, proveedor = null, onClose, onClasificada }) => {
  // `boleta` es la fila CRUDA de boletas_honorarios (el `raw` del doc — no se re-fetchea).
  const [tipoContable, setTipoContable] = useState(boleta.tipo_contable || "gasto");
  const [cuentaER, setCuentaER] = useState(boleta.cuenta_er || proveedor?.cuenta_er_default || "");
  const [recordar, setRecordar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Bloque B — path activo→CapEx. Al clasificar 'activo' se puede (a) vincular a un activo
  // planificado existente (reconcilia → estado 'activo' + fecha_compra = arranca la depreciación,
  // espejo de handleConfirmarActivo) o (b) crear un activo nuevo desde la boleta (espejo de
  // AsignarRecibidaModal). costo_unitario = monto_bruto (la BHE es exenta, el bruto ES el costo).
  const pais = boleta.pais || "Chile";
  const monedaLocal = pais === "Perú" ? "PEN" : "CLP";
  const [activoMode, setActivoMode] = useState("vincular"); // 'vincular' (reconcilia planificado) | 'crear'
  const [activosPlan, setActivosPlan] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [activoPlanSel, setActivoPlanSel] = useState("");
  const [nuevoTipoActivo, setNuevoTipoActivo] = useState("costo_instalacion");
  const [nuevoEdificioId, setNuevoEdificioId] = useState("");
  // C1 — vínculo ACTUAL: si la boleta ya cuelga de un activo (activos.boleta_honorario_id), se
  // muestra explícito con opción de desvincular, en vez del desplegable vacío. null = suelta.
  const [activoVinculado, setActivoVinculado] = useState(null);

  const nombre = boleta.emisor_nombre || "Honorario";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [planes, edifsRes, vincRes] = await Promise.all([
          getActivosPlanificados(pais),
          client.from("edificios").select("id, edificio").eq("pais", pais).order("edificio"),
          client
            .from("activos")
            .select("id, tipo_activo, costo_unitario, moneda, fecha_compra, estado, destino_id")
            .eq("boleta_honorario_id", boleta.id)
            .eq("eliminado", false),
        ]);
        if (cancel) return;
        setActivosPlan(planes || []);
        setEdificios(edifsRes.data || []);
        setActivoVinculado((vincRes.data || [])[0] || null);
        // Sin planificados → default a crear (no hay nada que reconciliar).
        if ((planes || []).length === 0) setActivoMode("crear");
      } catch {
        /* selector opcional: si falla la carga, queda el modo 'crear' sin lista de planificados */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [pais, boleta.id]);

  const handleGuardar = async () => {
    if (tipoContable === "gasto" && !cuentaER) {
      toast.error("Elegí una cuenta del ER para el gasto");
      return;
    }
    // Los validadores/alta del activo solo aplican si la boleta AÚN no cuelga de uno (activoVinculado
    // null). Si ya está vinculada, Guardar solo persiste la clasificación (no re-vincula ni duplica).
    if (tipoContable === "activo" && !activoVinculado && activoMode === "vincular" && !activoPlanSel) {
      toast.error("Elegí el activo planificado a vincular (o cambiá a 'Crear activo nuevo').");
      return;
    }
    if (tipoContable === "activo" && !activoVinculado && activoMode === "crear" && !nuevoTipoActivo) {
      toast.error("Elegí el tipo de activo.");
      return;
    }
    setGuardando(true);
    try {
      // Activo → cuenta_er en null (no tiene línea de costo; el motor la excluye por
      // tipo_contable). Gasto → la cuenta elegida. PostgREST NO lanza: se captura el {error}
      // (misma lección que 9.38 — un update fallido con toast.success engañoso).
      // Clasificación DELIBERADA per-documento → clasificacion_manual = true (override): no hay
      // auto-clasificación de honorarios, pero la marca deja el criterio homogéneo con recibidas.
      const cambios =
        tipoContable === "activo"
          ? { tipo_contable: "activo", cuenta_er: null, clasificacion_manual: true }
          : { tipo_contable: "gasto", cuenta_er: cuentaER, clasificacion_manual: true };
      const { error } = await client.from("boletas_honorarios").update(cambios).eq("id", boleta.id);
      if (error) throw error;

      // Bloque B — alta/reconcile del activo cuando la boleta se clasifica 'activo' y AÚN no cuelga
      // de uno. Si ya está vinculada (activoVinculado), no se re-vincula ni crea otro (evita duplicar);
      // para cambiar el activo el usuario desvincula primero. Reusa crearActivo/actualizarActivo (10.1).
      if (tipoContable === "activo" && !activoVinculado) {
        const fechaCompra = boleta.fecha_emision ? boleta.fecha_emision.substring(0, 10) : null;
        // 'vincular' reconcilia un planificado (estado 'activo' + fecha_compra → arranca la
        // depreciación; getDepreciacionMensual exige estado 'activo' + fecha_compra; espejo de
        // handleConfirmarActivo). 'crear' da de alta un activo nuevo desde la boleta (costo =
        // monto_bruto, exento; espejo de AsignarRecibidaModal.crearActivo).
        const reconcilePayload = {
          estado: "activo",
          fecha_compra: fechaCompra,
          boleta_honorario_id: boleta.id,
        };
        const nuevoActivoPayload = {
          pais,
          fecha_compra: fechaCompra,
          tipo_activo: nuevoTipoActivo,
          descripcion: nombre,
          numero_factura: `BHE ${boleta.folio}`,
          cantidad: 1,
          costo_unitario: Number(boleta.monto_bruto) || 0,
          moneda: monedaLocal,
          vida_util_meses: getVidaUtilPorTipo(nuevoTipoActivo),
          valor_residual: 0,
          estado: "activo",
          destino_tipo: nuevoEdificioId ? "edificio" : null,
          destino_id: nuevoEdificioId ? Number(nuevoEdificioId) : null,
          proveedor_id: boleta.proveedor_id || null,
          boleta_honorario_id: boleta.id,
        };
        await (activoMode === "vincular"
          ? actualizarActivo(Number(activoPlanSel), reconcilePayload)
          : crearActivo(nuevoActivoPayload));
      }

      // "Recordar" persiste el default al proveedor (por ID, regla 10.7), como recibidas.
      if (recordar && boleta.proveedor_id) {
        await actualizarProveedor(boleta.proveedor_id, {
          cuenta_er_default: tipoContable === "gasto" ? cuentaER : null,
          tipo_contable_default: tipoContable,
        });
      }

      toast.success("Boleta clasificada");
      onClasificada?.();
    } catch (err) {
      console.error("ClasificarHonorarioModal: error al guardar", err);
      toast.error("No se pudo guardar la clasificación");
    } finally {
      setGuardando(false);
    }
  };

  // C1 — Desvincular: el activo vuelve a 'planificado', se limpia fecha_compra + la FK →
  // deja de depreciar (getDepreciacionMensual exige estado 'activo' + fecha_compra). Espejo del
  // reverso de handleConfirmarActivo. Refresca la tabla (onClasificada) para que el eje "sin vincular"
  // quede consistente; el usuario re-vincula reabriendo el modal.
  const handleDesvincularActivo = async () => {
    if (!activoVinculado) return;
    if (
      !window.confirm(
        "¿Desvincular la boleta del activo? El activo vuelve a 'planificado', se limpia la fecha de compra y deja de depreciar."
      )
    )
      return;
    setGuardando(true);
    try {
      await actualizarActivo(activoVinculado.id, {
        estado: "planificado",
        fecha_compra: null,
        boleta_honorario_id: null,
      });
      toast.success("Boleta desvinculada del activo");
      onClasificada?.();
    } catch (err) {
      console.error("ClasificarHonorarioModal: error desvinculando activo", err);
      toast.error("No se pudo desvincular");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>
        Clasificar honorario — Boleta {boleta.folio} &middot; {nombre}
      </ModalHeader>
      <ModalBody>
        {/* Detalle compacto, una línea (mismo formato gris que el modal de recibidas). */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
          }}
        >
          <span>
            Bruto: <strong>{formatMonto(boleta.monto_bruto)}</strong>
          </span>
          <span>
            Retención: <strong>{formatMonto(boleta.monto_retencion)}</strong>
          </span>
          <span>
            Fecha: <strong>{boleta.fecha_emision ? boleta.fecha_emision.substring(0, 10) : "-"}</strong>
          </span>
        </div>

        {/* La BHE es exenta de IVA: el bruto ES el costo. Recordatorio de que la retención no
            baja el gasto (es pasivo tributario, vive en el bloque F29 de Caja). */}
        <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 14 }}>
          El bruto exento devenga como costo; la retención no baja el gasto (se entera al SII).
        </div>

        {/* Tipo contable. Default gasto, pero ~90% de los honorarios son instalación → activo. */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Tipo contable
          </label>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="tipoContableHon"
                value="gasto"
                checked={tipoContable === "gasto"}
                onChange={() => setTipoContable("gasto")}
                style={{ marginRight: 4 }}
              />
              Gasto (servicio)
            </label>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="tipoContableHon"
                value="activo"
                checked={tipoContable === "activo"}
                onChange={() => setTipoContable("activo")}
                style={{ marginRight: 4 }}
              />
              Activo (instalación)
            </label>
          </div>
        </div>

        {tipoContable === "gasto" ? (
          <div style={{ marginBottom: 4 }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Cuenta del Estado de Resultados
            </label>
            <select
              value={cuentaER}
              onChange={(e) => setCuentaER(e.target.value)}
              style={{
                width: "100%",
                height: 36,
                fontSize: "0.82rem",
                padding: "4px 10px",
                border: "1px solid #ced4da",
                borderRadius: 4,
              }}
            >
              <option value="">— Seleccionar cuenta —</option>
              {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
                <optgroup key={grupo} label={grupo}>
                  {cuentas.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ) : activoVinculado ? (
          // C1 — la boleta YA cuelga de un activo: mostrarlo explícito (nada de desplegable vacío que
          // deje al usuario adivinando) con opción de desvincular. Espejo de "Vinculación actual".
          <div
            style={{
              background: "#eafaf1",
              border: "1px solid #a9dfbf",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: "0.8rem",
            }}
          >
            <div style={{ fontWeight: 600, color: "#1e8449", marginBottom: 4 }}>
              Vinculada al activo #{activoVinculado.id}
            </div>
            <div style={{ color: "#555" }}>
              {[
                edificios.find((e) => e.id === activoVinculado.destino_id)?.edificio ||
                  (activoVinculado.destino_id ? `Edificio #${activoVinculado.destino_id}` : "Sin edificio"),
                activoVinculado.tipo_activo,
                formatMonto(activoVinculado.costo_unitario),
                activoVinculado.fecha_compra ? `compra ${activoVinculado.fecha_compra}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            <button
              type="button"
              onClick={handleDesvincularActivo}
              disabled={guardando}
              style={{
                marginTop: 8,
                background: "none",
                border: "1px solid #c0392b",
                borderRadius: 4,
                padding: "4px 12px",
                fontSize: "0.78rem",
                color: "#c0392b",
                cursor: guardando ? "not-allowed" : "pointer",
              }}
            >
              {guardando ? "…" : "Desvincular del activo"}
            </button>
          </div>
        ) : (
          // Path activo→CapEx (Bloque B): reconciliar un activo planificado (arranca depreciación)
          // o crear uno nuevo desde la boleta. La boleta igual sale de la línea de costo del ER.
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", marginBottom: 8 }}>
              <label
                style={{
                  cursor: activosPlan.length > 0 ? "pointer" : "not-allowed",
                  opacity: activosPlan.length > 0 ? 1 : 0.5,
                }}
              >
                <input
                  type="radio"
                  name="activoModeHon"
                  value="vincular"
                  checked={activoMode === "vincular"}
                  disabled={activosPlan.length === 0}
                  onChange={() => setActivoMode("vincular")}
                  style={{ marginRight: 4 }}
                />
                Vincular a planificado
              </label>
              <label style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="activoModeHon"
                  value="crear"
                  checked={activoMode === "crear"}
                  onChange={() => setActivoMode("crear")}
                  style={{ marginRight: 4 }}
                />
                Crear activo nuevo
              </label>
            </div>

            {activoMode === "vincular" ? (
              <select
                value={activoPlanSel}
                onChange={(e) => setActivoPlanSel(e.target.value)}
                style={{
                  width: "100%",
                  height: 36,
                  fontSize: "0.82rem",
                  padding: "4px 10px",
                  border: "1px solid #ced4da",
                  borderRadius: 4,
                }}
              >
                <option value="">— Elegí el activo planificado —</option>
                {activosPlan.map((a) => {
                  const edif = edificios.find((e) => e.id === a.destino_id);
                  const etiqueta = [
                    edif?.edificio || (a.destino_id ? `Edificio #${a.destino_id}` : null),
                    a.tipo_activo,
                    `$ ${Number(a.costo_unitario || 0).toLocaleString("es-CL")}`,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <option key={a.id} value={a.id}>
                      {etiqueta}
                    </option>
                  );
                })}
              </select>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={nuevoTipoActivo}
                  onChange={(e) => setNuevoTipoActivo(e.target.value)}
                  style={{
                    flex: 1,
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  {TIPOS_ACTIVO.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <select
                  value={nuevoEdificioId}
                  onChange={(e) => setNuevoEdificioId(e.target.value)}
                  style={{
                    flex: 1,
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— Edificio (opcional) —</option>
                  {edificios.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.edificio}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ fontSize: "0.72rem", color: "#909497", marginTop: 6 }}>
              {activoMode === "vincular"
                ? "El activo planificado pasa a 'activo' con fecha de compra = fecha de la boleta → arranca la depreciación."
                : `Se crea un activo por $ ${Number(boleta.monto_bruto || 0).toLocaleString("es-CL")} (bruto de la boleta), estado 'activo'.`}
            </div>
          </div>
        )}

        {boleta.proveedor_id && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", marginTop: 12 }}>
            <input type="checkbox" checked={recordar} onChange={(e) => setRecordar(e.target.checked)} />
            Recordar para {nombre}
          </label>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline size="sm" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClasificarHonorarioModal;
